import { useEffect, useReducer, useRef, useState } from 'react'

import { history } from '../../redux/store'
import { GoodsListParam } from '../../api/MaterialApi'
import {
  GET_CONTRACT,
  GET_FILE_STATE,
  DOWNLOAD_CONTRACT_FILE,
  POST_DOWNLOAD_CONTRACT_FILE,
  FileDetails,
} from '../../api/paymentOrder'
import {
  initialState,
  reducer,
  setDataSourceAction,
  setIsTableLoading,
  setPaginationOptionAction,
  setSearchParamAction,
  setSelectedRowKeysAction,
  setSelectedRowsAction,
  reloadStateAction,
} from './reducer'
import { isResponseCodeSuccess } from '../../tools/util'
import { usePageCache, useSkipInitEffect } from '../../tools/utilHooks'
import { message } from 'antd'
import { useLocation } from 'react-router-dom'
import { fileReviewStateEnum, signingTypeEnum } from '@/global/enum'
import { IMPORT_FILE } from '@/api/contractApi'
import { fileFile } from '@/modules/uploadfiles/hooks'

export const handleJumpAddInvoice = (orderIds: string[]) => {
  history.push({
    pathname: '/srm/addinvoice',
    state: {
      orderIds,
    },
  })
}

export function useSetupHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { paginationOption, searchParam, selectedRowKeys, selectedRows } = state
  const [showList, setShowList] = useState<boolean>(false)
  const [warningStatus, setWarningStatus] = useState<string>('empty')
  const [fileList, setFileList] = useState<any[]>([])
  const [fileStatus, setFileStatus] = useState<FileDetails>()
  const [reason, setReason] = useState<string>('')
  const [contractId, setContractId] = useState<string>('')
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  const { pathname } = useLocation()
  const searchRef = useRef(null)
  const uploadRef = useRef<HTMLElement>(null)
  const needRefresh = useRef<boolean>(false)
  const CACHE_KEY = pathname

  useEffect(() => {
    const flag = getCacheWithKey(CACHE_KEY)
    if (flag) {
      const action = reloadStateAction(flag)
      dispatch(action)
    } else {
      handlePaginationChange(1)
    }
  }, [])

  useSkipInitEffect(() => {
    // 缓存存数据
    setCacheWithKey(state, CACHE_KEY)
    // eslint-disable-next-line
  }, [state])

  async function handlePaginationChange(page: number, pageSize?: number) {
    await getPageData({
      pageNo: page,
      pageSize: pageSize ? pageSize : paginationOption.pagesize,
      ...searchParam,
    })
  }
  const handleTableChangeCheck = (
    selectedRowKeys: any,
    selectedRows: any[],
  ) => {
    dispatch(setSelectedRowsAction(selectedRows))
    dispatch(setSelectedRowKeysAction(selectedRowKeys))
  }

  function resetSelection() {
    dispatch(setSelectedRowsAction([]))
    dispatch(setSelectedRowKeysAction([]))
  }
  async function getPageData(params: GoodsListParam) {
    const {
      pageNo = paginationOption.current,
      pageSize = paginationOption.pagesize,
    } = params
    dispatch(setIsTableLoading(true))
    const res = await GET_CONTRACT({
      ...params,
      pageSize,
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setDataSourceAction(res.data.content))
      dispatch(
        setPaginationOptionAction({
          pagesize: pageSize,
          current: pageNo,
          total: res.data.totalCount,
        }),
      )
    }
    dispatch(setIsTableLoading(false))
  }
  const handleSearchClick = (value: any) => {
    getPageData({
      pageNo: 1,
      pageSize: paginationOption.pagesize,
      ...value,
    })
    dispatch(setSearchParamAction(value))
    resetSelection()
  }

  const getFileStatus = async (id: string) => {
    const res = await GET_FILE_STATE(id)
    if (isResponseCodeSuccess(res.code)) {
      setReason(res.data.reasonForRejection)
      setFileList(res.data.fileDTO ? [res.data.fileDTO] : [])
      setFileStatus(res.data)
      switch (res.data.fileReviewState) {
        case '1':
          setWarningStatus('empty')
          break
        case '2':
          setWarningStatus('loading')
          break
        case '3':
          setWarningStatus('fail')
          break
        case '4':
          setWarningStatus('success')
          break
        case '5':
        default:
          setWarningStatus('manReview')
          break
      }
    }
  }

  const downloadFile = async () => {
    if (!selectedRowKeys.length) {
      message.error('请先勾选数据')
      return
    }
    for (const item of selectedRows) {
      if (item.reportReviewState === '审核中') {
        message.error(
          `${item.contractNo}对应的入驻报备单${
            item.registrationNumber || ''
          }处于审核中，不可下载`,
        )
        return
      }
    }

    // if (
    //   selectedRows.some((item) => {
    //     return signingTypeEnum.isLabelIn(item.signingType!, ['全电子合同'])
    //   })
    // ) {
    //   message.warning('全电子合同不可下载合同进行签署！')
    //   return
    // }
    // 不同签订方式合同不可以一起下载
    const signingTypeSet = new Set()
    selectedRows.forEach((item) => {
      signingTypeSet.add(item.signingType)
    })
    if (signingTypeSet.size > 1) {
      message.warning('不同签订方式合同不可以一起下载')
      return
    }
    const res = await POST_DOWNLOAD_CONTRACT_FILE(selectedRowKeys)
    if (res.code === 0) {
      message.success('已转移至任务中心执行')
    }
  }

  const getAttachment = (record: any, status: string) => {
    setShowList(true)
    setWarningStatus(status)
    getFileStatus(record.id)
    setContractId(record.id)
  }

  // 表格内文件上传处理
  const inTableUploadfile = async (options: {
    e: fileFile[]
    isOld: boolean
    id: string
    record: any
  }) => {
    const { e, isOld, id, record } = options
    // 上传当前合同的附件
    if (e.length) {
      if (isOld) {
        const res = await IMPORT_FILE({
          fileId: e[0].id as string,
          contractId: id,
          // contractStatus: '4',
        })
        if (isResponseCodeSuccess(res.code)) {
          message.success('上传成功')
        }
        getPageData({
          pageNo: paginationOption.current,
          pageSize: paginationOption.pagesize,
          ...searchParam,
        })
        return
      }
      const hide = message.loading('文件上传中...', 0)
      try {
        const res = await IMPORT_FILE({
          fileId: e[0].id as string,
          contractId: id || record.id,
        })
        if (isResponseCodeSuccess(res.code)) {
          message.success('上传成功')
        } else {
          message.error(res.msg || '上传失败')
        }
      } catch (error) {
        console.error('文件上传失败:', error)
        message.error('文件上传失败')
      }
      hide()
    } else {
      // 表格内删除附件会调用组件内部删除接口,只需要重新获取页面数据即可
      message.destroy()
    }
    // 删除或新增后重新获取数据
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }
  async function handleRefreshData() {
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }
  const cancelUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    setShowList(false)
  }
  const confirmUpload = async () => {
    console.log({ fileList })

    if (warningStatus === 'loading') {
      return
    }
    // 未通过需要重新触发审核
    if (
      fileReviewStateEnum.isValueInLabels(fileStatus?.fileReviewState, [
        '未通过',
      ])
    ) {
      if (fileList.length > 0) {
        await IMPORT_FILE({
          fileId: fileList?.[0]?.id || '',
          contractId: contractId,
          resubmit: true,
        })
      }
    }

    getFileStatus(contractId)
    handleRefreshData()

    setShowList(false)
  }
  const fileListChange = async (e: fileFile[]) => {
    setFileList(e)
    needRefresh.current = true
    if (e[0]) {
      // setWarningStatus('loading')
      // const res = await GET_CONTRACT_V2({
      //   fileUrl: e[0].url,
      //   contractId,
      // })
      const res2 = await IMPORT_FILE({
        fileId: e[0].id as string,
        contractId,
        resubmit: fileReviewStateEnum.isValueInLabels(
          fileStatus?.fileReviewState,
          ['未通过'],
        ),
        // contractStatus: res.data ? '4' : '2',
      })
      getFileStatus(contractId)
    } else {
      setWarningStatus('empty')
    }
  }
  return {
    state,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    handleJumpAddInvoice,
    getFileStatus,
    showList,
    setShowList,
    warningStatus,
    setWarningStatus,
    fileList,
    setFileList,
    reason,
    searchRef,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    setContractId,
    fileStatus,
    fileListChange,
    uploadRef,
    cancelUpload,
    confirmUpload,
  }
}
import { useEffect, useReducer, useRef, useState } from 'react'

import { history } from '../../redux/store'
import { GoodsListParam } from '../../api/MaterialApi'
import {
  GET_CONTRACT,
  GET_FILE_STATE,
  DOWNLOAD_CONTRACT_FILE,
  POST_DOWNLOAD_CONTRACT_FILE,
  FileDetails,
} from '../../api/paymentOrder'
import {
  initialState,
  reducer,
  setDataSourceAction,
  setIsTableLoading,
  setPaginationOptionAction,
  setSearchParamAction,
  setSelectedRowKeysAction,
  setSelectedRowsAction,
  reloadStateAction,
} from './reducer'
import { isResponseCodeSuccess } from '../../tools/util'
import { usePageCache, useSkipInitEffect } from '../../tools/utilHooks'
import { message } from 'antd'
import { useLocation } from 'react-router-dom'
import { fileReviewStateEnum, signingTypeEnum } from '@/global/enum'
import { IMPORT_FILE } from '@/api/contractApi'
import { fileFile } from '@/modules/uploadfiles/hooks'

export const handleJumpAddInvoice = (orderIds: string[]) => {
  history.push({
    pathname: '/srm/addinvoice',
    state: {
      orderIds,
    },
  })
}

export function useSetupHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { paginationOption, searchParam, selectedRowKeys, selectedRows } = state
  const [showList, setShowList] = useState<boolean>(false)
  const [warningStatus, setWarningStatus] = useState<string>('empty')
  const [fileList, setFileList] = useState<any[]>([])
  const [fileStatus, setFileStatus] = useState<FileDetails>()
  const [reason, setReason] = useState<string>('')
  const [contractId, setContractId] = useState<string>('')
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  const { pathname } = useLocation()
  const searchRef = useRef(null)
  const uploadRef = useRef<HTMLElement>(null)
  const needRefresh = useRef<boolean>(false)
  const CACHE_KEY = pathname

  useEffect(() => {
    const flag = getCacheWithKey(CACHE_KEY)
    if (flag) {
      const action = reloadStateAction(flag)
      dispatch(action)
    } else {
      handlePaginationChange(1)
    }
  }, [])

  useSkipInitEffect(() => {
    // 缓存存数据
    setCacheWithKey(state, CACHE_KEY)
    // eslint-disable-next-line
  }, [state])

  async function handlePaginationChange(page: number, pageSize?: number) {
    await getPageData({
      pageNo: page,
      pageSize: pageSize ? pageSize : paginationOption.pagesize,
      ...searchParam,
    })
  }
  const handleTableChangeCheck = (
    selectedRowKeys: any,
    selectedRows: any[],
  ) => {
    dispatch(setSelectedRowsAction(selectedRows))
    dispatch(setSelectedRowKeysAction(selectedRowKeys))
  }

  function resetSelection() {
    dispatch(setSelectedRowsAction([]))
    dispatch(setSelectedRowKeysAction([]))
  }
  async function getPageData(params: GoodsListParam) {
    const {
      pageNo = paginationOption.current,
      pageSize = paginationOption.pagesize,
    } = params
    dispatch(setIsTableLoading(true))
    const res = await GET_CONTRACT({
      ...params,
      pageSize,
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setDataSourceAction(res.data.content))
      dispatch(
        setPaginationOptionAction({
          pagesize: pageSize,
          current: pageNo,
          total: res.data.totalCount,
        }),
      )
    }
    dispatch(setIsTableLoading(false))
  }
  const handleSearchClick = (value: any) => {
    getPageData({
      pageNo: 1,
      pageSize: paginationOption.pagesize,
      ...value,
    })
    dispatch(setSearchParamAction(value))
    resetSelection()
  }

  const getFileStatus = async (id: string) => {
    const res = await GET_FILE_STATE(id)
    if (isResponseCodeSuccess(res.code)) {
      setReason(res.data.reasonForRejection)
      setFileList(res.data.fileDTO ? [res.data.fileDTO] : [])
      setFileStatus(res.data)
      switch (res.data.fileReviewState) {
        case '1':
          setWarningStatus('empty')
          break
        case '2':
          setWarningStatus('loading')
          break
        case '3':
          setWarningStatus('fail')
          break
        case '4':
          setWarningStatus('success')
          break
        case '5':
        default:
          setWarningStatus('manReview')
          break
      }
    }
  }

  const downloadFile = async () => {
    if (!selectedRowKeys.length) {
      message.error('请先勾选数据')
      return
    }
    for (const item of selectedRows) {
      if (item.reportReviewState === '审核中') {
        message.error(
          `${item.contractNo}对应的入驻报备单${
            item.registrationNumber || ''
          }处于审核中，不可下载`,
        )
        return
      }
    }

    // if (
    //   selectedRows.some((item) => {
    //     return signingTypeEnum.isLabelIn(item.signingType!, ['全电子合同'])
    //   })
    // ) {
    //   message.warning('全电子合同不可下载合同进行签署！')
    //   return
    // }
    // 不同签订方式合同不可以一起下载
    const signingTypeSet = new Set()
    selectedRows.forEach((item) => {
      signingTypeSet.add(item.signingType)
    })
    if (signingTypeSet.size > 1) {
      message.warning('不同签订方式合同不可以一起下载')
      return
    }
    const res = await POST_DOWNLOAD_CONTRACT_FILE(selectedRowKeys)
    if (res.code === 0) {
      message.success('已转移至任务中心执行')
    }
  }

  const getAttachment = (record: any, status: string) => {
    setShowList(true)
    setWarningStatus(status)
    getFileStatus(record.id)
    setContractId(record.id)
  }

  // 表格内文件上传处理
  const inTableUploadfile = async (options: {
    e: fileFile[]
    isOld: boolean
    id: string
    record: any
  }) => {
    const { e, isOld, id, record } = options
    // 上传当前合同的附件
    if (e.length) {
      if (isOld) {
        const res = await IMPORT_FILE({
          fileId: e[0].id as string,
          contractId: id,
          // contractStatus: '4',
        })
        if (isResponseCodeSuccess(res.code)) {
          message.success('上传成功')
        }
        getPageData({
          pageNo: paginationOption.current,
          pageSize: paginationOption.pagesize,
          ...searchParam,
        })
        return
      }
      const hide = message.loading('文件上传中...', 0)
      try {
        const res = await IMPORT_FILE({
          fileId: e[0].id as string,
          contractId: id || record.id,
        })
        if (isResponseCodeSuccess(res.code)) {
          message.success('上传成功')
        } else {
          message.error(res.msg || '上传失败')
        }
      } catch (error) {
        console.error('文件上传失败:', error)
        message.error('文件上传失败')
      }
      hide()
    } else {
      // 表格内删除附件会调用组件内部删除接口,只需要重新获取页面数据即可
      message.destroy()
    }
    // 删除或新增后重新获取数据
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }
  async function handleRefreshData() {
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }
  const cancelUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    setShowList(false)
  }
  const confirmUpload = async () => {
    console.log({ fileList })

    if (warningStatus === 'loading') {
      return
    }
    // 未通过需要重新触发审核
    if (
      fileReviewStateEnum.isValueInLabels(fileStatus?.fileReviewState, [
        '未通过',
      ])
    ) {
      if (fileList.length > 0) {
        await IMPORT_FILE({
          fileId: fileList?.[0]?.id || '',
          contractId: contractId,
          resubmit: true,
        })
      }
    }

    getFileStatus(contractId)
    handleRefreshData()

    setShowList(false)
  }
  const fileListChange = async (e: fileFile[]) => {
    setFileList(e)
    needRefresh.current = true
    if (e[0]) {
      // setWarningStatus('loading')
      // const res = await GET_CONTRACT_V2({
      //   fileUrl: e[0].url,
      //   contractId,
      // })
      const res2 = await IMPORT_FILE({
        fileId: e[0].id as string,
        contractId,
        resubmit: fileReviewStateEnum.isValueInLabels(
          fileStatus?.fileReviewState,
          ['未通过'],
        ),
        // contractStatus: res.data ? '4' : '2',
      })
      getFileStatus(contractId)
    } else {
      setWarningStatus('empty')
    }
  }
  return {
    state,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    handleJumpAddInvoice,
    getFileStatus,
    showList,
    setShowList,
    warningStatus,
    setWarningStatus,
    fileList,
    setFileList,
    reason,
    searchRef,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    setContractId,
    fileStatus,
    fileListChange,
    uploadRef,
    cancelUpload,
    confirmUpload,
  }
}
